import React, { useEffect, Suspense, lazy } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import Header from "@/components/layout/Header";
import ScrollToTop from "@/components/ScrollToTop";
import Footer from "@/components/layout/Footer";
import AnnouncementBar from "@/components/AnnouncementBar";
// Popups + toasts + secondary chrome are LAZY — they never render on the
// critical path (mount after idle / on trigger) so keeping them out of
// main.js knocks ~40 KiB off the initial parse and helps TBT. Sept 2026
// perf regression fix — homepage TBT was 1600ms with 20 long tasks.
const EmailCapturePopup = lazy(() => import("@/components/popups/EmailCapturePopup"));
const ExitIntentPopup = lazy(() => import("@/components/ExitIntentPopup"));
const SubscriptionUpsellPopup = lazy(() => import("@/components/popups/SubscriptionUpsellPopup"));
const WaitlistExitIntentPopup = lazy(() => import("@/components/popups/WaitlistExitIntentPopup"));
const JustOrderedToast = lazy(() => import("@/components/JustOrderedToast"));
const MobileContactFab = lazy(() => import("@/components/MobileContactFab"));
const MobileCartBar = lazy(() => import("@/components/MobileCartBar"));

// Analytics libs are DYNAMICALLY imported inside a rIC/setTimeout so their
// bytes never land in main.js and their init cost is off the main render
// path. Feb 2026 TBT pass — moved from static top-of-file imports.
// Previously each of metaPixel + clarity + pageviewTracker got parsed
// during hydration even though the code was idle-guarded.

// Eagerly-loaded hot paths (visible on first load / SEO landing pages)
// KEEP MINIMAL — every eager import lands in main.js and blocks TTI.
// Aug 2026 perf pass: dropped 12 pages from eager → lazy (BoxDetail,
// ProductDetail, BuildYourOwn, Cart, HowItWorks, FAQ, Contact, HalalMeat,
// FloorPack, FloorPickSlip, FloorOrderSlip, FloorDeliver, OpsRoute,
// AuthCallback, NotFound). Only Home + Boxes stay eager because those
// are the two highest-impression landing routes for paid traffic.
import Home from "@/pages/Home";
import Boxes from "@/pages/Boxes";
import NotFound from "@/pages/NotFound";
import { lazyRetry } from "@/lib/lazyRetry";
import { useAuth } from "@/context/AuthContext";
import { markInternal } from "@/lib/session";

// Lazy-loaded — split off the main consumer bundle. Each route gets its
// own chunk that only downloads when the customer navigates there. This
// alone cut main.js by ~240 KB (measured Aug 2026 after the fonts fix
// but before this pass).
// Oct 2026 — every lazy import goes through `lazyRetry`: a failed /
// timed-out chunk fetch (Clarity logged "Loading chunk 309 failed
// (timeout)" in FB/IG in-app browsers) retries once, then reloads the
// page once with a cache-busted index.html instead of a blank screen.
const BoxDetail = lazyRetry(() => import("@/pages/BoxDetail"), "BoxDetail");
const ProductDetail = lazyRetry(() => import("@/pages/ProductDetail"), "ProductDetail");
const BuildYourOwn = lazyRetry(() => import("@/pages/BuildYourOwn"), "BuildYourOwn");
const Cart = lazyRetry(() => import("@/pages/Cart"), "Cart");
const HowItWorks = lazyRetry(() => import("@/pages/HowItWorks"), "HowItWorks");
const FAQ = lazyRetry(() => import("@/pages/FAQ"), "FAQ");
const Contact = lazyRetry(() => import("@/pages/Contact"), "Contact");
const HalalMeat = lazyRetry(() => import("@/pages/HalalMeat"), "HalalMeat");
const MeatDeliveryPerth = lazyRetry(() => import("@/pages/MeatDeliveryPerth"), "MeatDeliveryPerth");
const SouthAfricanButcherPerth = lazyRetry(() => import("@/pages/SouthAfricanButcherPerth"), "SouthAfricanButcherPerth");
const FloorPack = lazyRetry(() => import("@/pages/FloorPack"), "FloorPack");
const FloorPickSlip = lazyRetry(() => import("@/pages/FloorPickSlip"), "FloorPickSlip");
const FloorOrderSlip = lazyRetry(() => import("@/pages/FloorOrderSlip"), "FloorOrderSlip");
const FloorPackSlips = lazyRetry(() => import("@/pages/FloorPackSlips"), "FloorPackSlips");
const FloorMap = lazyRetry(() => import("@/pages/FloorMap"), "FloorMap");
const FloorDeliver = lazyRetry(() => import("@/pages/FloorDeliver"), "FloorDeliver");
const DriverRun = lazyRetry(() => import("@/pages/DriverRun"), "DriverRun");
const OpsRoute = lazyRetry(() => import("@/pages/OpsRoute"), "OpsRoute");
const AuthCallback = lazyRetry(() => import("@/pages/AuthCallback"), "AuthCallback");
const CheckoutSuccess = lazyRetry(() => import("@/pages/CheckoutSuccess"), "CheckoutSuccess");
const CheckoutElements = lazyRetry(() => import("@/pages/CheckoutElements"), "CheckoutElements");
const Wholesale = lazyRetry(() => import("@/pages/Wholesale"), "Wholesale");
const Export = lazyRetry(() => import("@/pages/Export"), "Export");
const Login = lazyRetry(() => import("@/pages/Login"), "Login");
const Register = lazyRetry(() => import("@/pages/Register"), "Register");
const ForgotPassword = lazyRetry(() => import("@/pages/ForgotPassword"), "ForgotPassword");
const ResetPassword = lazyRetry(() => import("@/pages/ResetPassword"), "ResetPassword");
const Account = lazyRetry(() => import("@/pages/Account"), "Account");
const CancelWhy = lazyRetry(() => import("@/pages/CancelWhy"), "CancelWhy");
const SmsOptOut = lazyRetry(() => import("@/pages/SmsOptOut"), "SmsOptOut");
const Admin = lazyRetry(() => import("@/pages/Admin"), "Admin");
const SeoLanding = lazyRetry(() => import("@/pages/SeoLanding"), "SeoLanding");
import { SUBURBS } from "@/data/suburbs";
const SUBURB_SLUGS = Object.keys(SUBURBS);
const Blog = lazyRetry(() => import("@/pages/Blog"), "Blog");
const BlogPost = lazyRetry(() => import("@/pages/BlogPost"), "BlogPost");

function RouteFallback() {
  // First paint after a prerendered load: reserve the exact height the
  // static HTML occupied so the footer doesn't move while the route
  // chunk downloads. Otherwise reserve a full viewport — P6 (Oct 2026):
  // the old 60vh left ~50-80px of footer visible at the bottom of a
  // 390×844 phone, and the footer was then slammed down when the lazy
  // route chunk arrived (CLS 0.04 on detail pages, 0.09 on /cart).
  const reserved = typeof window !== "undefined" ? window.__tbbPrerenderMainHeight : 0;
  useEffect(() => () => { if (typeof window !== "undefined") window.__tbbPrerenderMainHeight = 0; }, []);
  return (
    <div
      className="flex items-center justify-center text-sm text-[color:var(--tbb-muted)]"
      style={{ minHeight: reserved > 0 ? `${reserved}px` : "100vh" }}
      data-testid="route-suspense-fallback"
    >
      Loading…
    </div>
  );
}

function App() {
  const location = useLocation();
  const { user } = useAuth() || {};
  // Oct 2026 — internal-traffic filter. Admin logins and any /admin visit
  // mark the tab session internal: Clarity gets role=admin (filterable),
  // and every pageview / funnel beacon carries `internal: true` so the
  // server-side funnel excludes it.
  useEffect(() => {
    const isAdminUser = user?.role === "admin";
    const onAdminRoute = location.pathname.startsWith("/admin");
    if (!isAdminUser && !onAdminRoute) return;
    markInternal(isAdminUser ? "admin" : "admin-route");
    import("@/lib/clarity").then(({ tagClarityInternal }) => tagClarityInternal(user?.email)).catch(() => {});
  }, [user?.role, user?.email, location.pathname]);

  // Meta Pixel + Microsoft Clarity: DYNAMIC-import both libs inside a
  // requestIdleCallback so the bytes never enter main.js and the init
  // never competes with React hydration for main-thread time. This drops
  // INP by ~60-100ms on cold loads AND removes ~14 KB from main.js
  // (which was previously being parsed even though init was idle-guarded).
  useEffect(() => {
    const boot = async () => {
      try {
        const [{ initPixel }, { initClarity }, { initClsMonitor }] = await Promise.all([
          import("@/lib/metaPixel"),
          import("@/lib/clarity"),
          import("@/lib/clsMonitor"),
        ]);
        initPixel();
        initClarity();
        // CLS monitor MUST run on every route — the observer is scoped
        // to this page load, and the beacon fires on visibilitychange.
        initClsMonitor();
      } catch { /* analytics is best-effort */ }
    };
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      const handle = window.requestIdleCallback(boot, { timeout: 3000 });
      return () => window.cancelIdleCallback?.(handle);
    }
    const t = setTimeout(boot, 1500);
    return () => clearTimeout(t);
  }, []);

  // Warm the /api/boxes and /api/products caches during idle time on the
  // home page (and any other landing route) so that navigations INTO
  // /boxes and /build-your-own start with the response already in the
  // browser cache. Removes the ~200-500 ms fetch delay that lets the
  // product grid pop-in and slam the footer down (P2 root cause on the
  // returning-visit path). Cold loads directly onto /boxes or /build-
  // your-own still rely on the min-h reservations in those pages plus
  // the prerender step (scripts/prerender.mjs) to hit CLS=0.
  useEffect(() => {
    if (typeof window === "undefined" || !window.fetch) return;
    // Only warm on entry routes — no need to hit /api again when we're
    // already ON /boxes or /build-your-own.
    const prefetchRoutes = new Set(["/", "/how-it-works", "/blog", "/contact"]);
    if (!prefetchRoutes.has(location.pathname)) return;
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (!backend) return;
    const warm = () => {
      try {
        // Fire and forget — response lands in HTTP cache. If the endpoint
        // is 500ing we swallow silently; the target pages have their own
        // /api call which will surface the error at the right time.
        fetch(`${backend}/api/boxes`, { credentials: "omit" }).catch(() => {});
        fetch(`${backend}/api/products`, { credentials: "omit" }).catch(() => {});
      } catch { /* noop */ }
    };
    if ("requestIdleCallback" in window) {
      const h = window.requestIdleCallback(warm, { timeout: 4000 });
      return () => window.cancelIdleCallback?.(h);
    }
    const t = setTimeout(warm, 2000);
    return () => clearTimeout(t);
  }, [location.pathname]);

  // Page-view tracking (Meta Pixel PageView + self-hosted /track/pageview).
  // Both are fire-and-forget beacons; we yield them off the route-change
  // tick with requestIdleCallback so they don't stretch a paint into a
  // long task on slow mobiles. Dynamically-imported so the tracker code
  // isn't parsed until it's needed.
  useEffect(() => {
    let cancelled = false;
    const fire = async () => {
      if (cancelled) return;
      try {
        const [{ trackPageView }, { trackServerPageview }] = await Promise.all([
          import("@/lib/metaPixel"),
          import("@/lib/pageviewTracker"),
        ]);
        if (cancelled) return;
        trackPageView();
        trackServerPageview(location.pathname);
      } catch { /* silent */ }
    };
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      const handle = window.requestIdleCallback(fire, { timeout: 2000 });
      return () => { cancelled = true; window.cancelIdleCallback?.(handle); };
    }
    const t = setTimeout(fire, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [location.pathname]);

  // Persist inbound ?ref=CODE query param so it survives navigation until the
  // visitor registers or checks out.
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const ref = (url.searchParams.get("ref") || "").trim().toUpperCase();
      if (ref) localStorage.setItem("tbb_ref", ref);
    } catch { /* noop */ }
  }, [location.pathname]);
  // Emergent OAuth returns with #session_id=... — handle synchronously
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // Oct 2026 chrome brief: no rotating announcement bar on /cart + checkout.
  const checkoutChrome = location.pathname === "/cart" || location.pathname.startsWith("/checkout");
  const hideChrome = location.pathname === "/auth/callback"
    || location.pathname.startsWith("/pack/")
    || location.pathname.startsWith("/deliver/")
    || location.pathname.startsWith("/drive/")
    || location.pathname.startsWith("/ops/");
  return (
    <div className="min-h-screen bg-white text-[color:var(--tbb-ink)]">
      <ScrollToTop />
      {!hideChrome && !checkoutChrome && <AnnouncementBar />}
      {!hideChrome && <Header />}
      <main>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/boxes" element={<Boxes />} />
            <Route path="/boxes/:slug" element={<BoxDetail />} />
            <Route path="/product/:slug" element={<ProductDetail />} />
            <Route path="/build-your-own" element={<BuildYourOwn />} />
            <Route path="/build-your-own/:categorySlug" element={<BuildYourOwn />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout" element={<CheckoutElements />} />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/wholesale" element={<Wholesale />} />
            <Route path="/halal-meat-delivery-perth" element={<HalalMeat />} />
            <Route path="/meat-delivery-perth" element={<MeatDeliveryPerth />} />
            <Route path="/south-african-butcher-perth" element={<SouthAfricanButcherPerth />} />
            <Route path="/biltong-perth" element={<SouthAfricanButcherPerth />} />
            <Route path="/perth-freezer" element={<SeoLanding forcedSlug="perth-freezer" />} />
            {/* Feb 2026: top-level SEO landings — "seo/" in the path signals
                doorway pages, so these get first-class routes. Kept
                short + descriptive. Old /seo/... URLs redirect to
                these via the /seo/:slug router below. */}
            <Route path="/meat-delivery-bunbury" element={<SeoLanding forcedSlug="meat-delivery-bunbury" />} />
            <Route path="/meat-subscription-box" element={<SeoLanding forcedSlug="meat-subscription-box" />} />
            <Route path="/bbq-meat-box" element={<SeoLanding forcedSlug="bbq-meat-box" />} />
            {/* Sep 2026: intent-based category landings promoted out of /seo/.
                Each maps to a static forcedSlug — no parametric pattern
                since these slugs don't share a common prefix. */}
            <Route path="/beef-delivery" element={<SeoLanding forcedSlug="beef-delivery" />} />
            <Route path="/lamb-delivery" element={<SeoLanding forcedSlug="lamb-delivery" />} />
            <Route path="/chicken-delivery" element={<SeoLanding forcedSlug="chicken-delivery" />} />
            <Route path="/family-meat-box" element={<SeoLanding forcedSlug="family-meat-box" />} />
            <Route path="/carnivore-meat-box" element={<SeoLanding forcedSlug="carnivore-meat-box" />} />
            <Route path="/build-your-own-meat-box" element={<SeoLanding forcedSlug="build-your-own-meat-box" />} />
            {/* Sep 2026: every /seo/meat-delivery-<suburb> was promoted to
                its clean top-level URL. React Router 7 doesn't allow a
                param prefix INSIDE a segment (path="/meat-delivery-:suburb"
                silently 404s), so we enumerate every suburb slug from
                data/suburbs.js. Adds one Route per suburb — 30 lines —
                but bulletproof, code-shared with the SEO_PAGES lookup,
                and requires zero maintenance when a suburb is added. */}
            {SUBURB_SLUGS.map((slug) => (
              slug === "meat-delivery-bunbury" ? null : (
                <Route
                  key={slug}
                  path={`/${slug}`}
                  element={<SeoLanding forcedSlug={slug} />}
                />
              )
            ))}
            <Route path="/pack/:token" element={<FloorPack />} />
            <Route path="/pack/:token/pick-slip" element={<FloorPickSlip />} />
            <Route path="/pack/:token/orders/:orderId/slip" element={<FloorOrderSlip />} />
            <Route path="/pack/:token/slips" element={<FloorPackSlips />} />
            <Route path="/pack/:token/map" element={<FloorMap />} />
            <Route path="/deliver/:token" element={<FloorDeliver />} />
            <Route path="/drive/:token/:driver" element={<DriverRun />} />
            <Route path="/ops/route/:token" element={<OpsRoute />} />
            <Route path="/export" element={<Export />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/account/*" element={<Account />} />
            <Route path="/why/:token" element={<CancelWhy />} />
            <Route path="/s/:token" element={<SmsOptOut />} />
            <Route path="/admin/*" element={<Admin />} />
            <Route path="/seo/:slug" element={<SeoLanding />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      {!hideChrome && <Footer />}
      {/* Lazy popup shell — all these mount post-idle. Suspense fallback
          is null (no spinner) since they're non-critical UI. Keeping
          the fallback null also means React doesn't schedule any work
          for them before they're actually needed. */}
      <Suspense fallback={null}>
        <EmailCapturePopup />
        {!hideChrome && <ExitIntentPopup />}
        <SubscriptionUpsellPopup />
        {!hideChrome && <WaitlistExitIntentPopup />}
        <JustOrderedToast />
        {!hideChrome && <MobileContactFab />}
        {!hideChrome && <MobileCartBar />}
      </Suspense>
    </div>
  );
}

export default App;
